/**
 * <modulus-video> — industrial-minimal video player.
 *
 * Behaviors:
 *   - source=uploaded, autoplay=true  → native <video> plays on load.
 *   - source=uploaded, autoplay=false → click play button calls video.play().
 *   - source=url,      autoplay=true  → iframe inserted immediately.
 *   - source=url,      autoplay=false → poster + play button; click swaps in
 *                                       the iframe (lite-embed, zero network
 *                                       cost until interaction).
 */

class ModulusVideo extends HTMLElement {
  connectedCallback() {
    const autoplay = this.dataset.autoplay === 'true';
    const source = this.dataset.source;

    if (source === 'url' && autoplay && this.dataset.videoId) {
      this.#loadIframe();
      return;
    }

    const playButton = this.querySelector('.modulus-video__play');
    if (playButton) {
      playButton.addEventListener('click', this.#onPlayClick);
    }
  }

  #onPlayClick = (event) => {
    event.preventDefault();
    const source = this.dataset.source;

    if (source === 'uploaded') {
      const video = this.querySelector('video');
      if (!video) return;
      // Reveal native controls (if absent) once the user has chosen to play
      video.setAttribute('controls', '');
      video.play().catch(() => {});
      this.setAttribute('data-playing', '');
      return;
    }

    if (source === 'url') {
      this.#loadIframe();
    }
  };

  #loadIframe() {
    if (this.querySelector('iframe')) return;

    const { videoId, videoType, loop } = this.dataset;
    if (!videoId || !videoType) return;

    const src = this.#buildEmbedUrl(videoType, videoId, loop === 'true');
    if (!src) return;

    const iframe = document.createElement('iframe');
    iframe.src = src;
    iframe.className = 'modulus-video__iframe';
    iframe.loading = 'lazy';
    iframe.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.setAttribute('title', 'Video player');

    this.appendChild(iframe);
    this.setAttribute('data-playing', '');

    const playButton = this.querySelector('.modulus-video__play');
    if (playButton) playButton.remove();
  }

  #buildEmbedUrl(type, id, loop) {
    if (type === 'youtube') {
      const params = new URLSearchParams({
        autoplay: '1',
        playsinline: '1',
        rel: '0',
        modestbranding: '1',
      });
      if (loop) {
        params.set('loop', '1');
        params.set('playlist', id);
      }
      return `https://www.youtube-nocookie.com/embed/${id}?${params.toString()}`;
    }

    if (type === 'vimeo') {
      const params = new URLSearchParams({
        autoplay: '1',
        playsinline: '1',
        dnt: '1',
      });
      if (loop) params.set('loop', '1');
      return `https://player.vimeo.com/video/${id}?${params.toString()}`;
    }

    return null;
  }
}

if (!customElements.get('modulus-video')) {
  customElements.define('modulus-video', ModulusVideo);
}
